import React, { useState } from "react";

import ChevronDownIcon from "@uui/ChevronDown";
import { isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import type { UserDistributionSetting } from "@app/common/types";

import { Column, Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  EnabledChannels,
} from "./styles";

interface ReportingSettingAccordionProps {
  title: string;
  subtitle: string;
  enabled?: boolean;
  enabledChannels?: UserDistributionSetting[];
  onToggleEnabled: (enable: boolean) => void;
  alerts?: React.ReactNode[];
  opts?: {
    expandable?: boolean;
    disabled: boolean;
    disabledTooltip: string;
  };
  children: React.ReactNode;
}

const ReportingSettingAccordion = ({
  title,
  subtitle,
  enabled = false,
  enabledChannels,
  onToggleEnabled,
  alerts = [],
  opts,
  children,
}: ReportingSettingAccordionProps) => {
  const [expanded, setExpanded] = useState(false);

  const isDisabled = Boolean(opts?.disabled);
  const settingIsEnabled = !isEmpty(enabledChannels) || enabled;
  const expandable = (opts?.expandable ?? true) && !isDisabled;

  const handleClickSummary = (event: React.SyntheticEvent) => {
    // don't toggle the accordion expand every time you click a toggle
    // event.stopPropagation doesn't work here for some reason.
    if ((event.target as HTMLElement).tagName === "INPUT" || isDisabled) {
      return;
    }

    setExpanded((e) => !e);
  };

  const handleToggleEnabled = (
    event: React.SyntheticEvent,
    checked: boolean
  ) => {
    onToggleEnabled(checked);
    if (checked && expandable) setExpanded(true);
  };

  return (
    <Accordion expanded={expandable && expanded}>
      <AccordionSummary
        onClick={handleClickSummary}
        expandable={expandable}
        expandIcon={null}
      >
        <Column spacing={1.5} width="100%">
          <Row variant="apart" spacing={2} flex={1}>
            <Column spacing={0.5}>
              <Row spacing={1} variant="left">
                <Typography variant="bodyPrimaryMed">{title}</Typography>
                {expandable && (
                  <ChevronDownIcon className="reporting-setting-accordion__expand-icon" />
                )}
              </Row>
              {subtitle && (
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.500"
                >
                  {subtitle}
                </Typography>
              )}
            </Column>
            <Row variant="right" spacing={2}>
              {enabledChannels && (
                <EnabledChannels settings={enabledChannels} />
              )}
              <TooltipWrapper show={isDisabled} title={opts?.disabledTooltip}>
                <Switch
                  disabled={isDisabled}
                  withStatusLabel
                  checked={settingIsEnabled}
                  onChange={handleToggleEnabled}
                />
              </TooltipWrapper>
            </Row>
          </Row>
          {alerts}
        </Column>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

export default ReportingSettingAccordion;
